import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import {
  CreateReportDto,
  RenameReportDto,
  ReportDto,
  ReportListItemDto,
  ReportPeriodDto,
} from '@anessa/shared-models';
import { firstValueFrom } from 'rxjs';
import { Project } from '../../../shared/services/project/project.model';
import { ProjectService } from '../../../shared/services/project/project.service';
import { IdValidation } from '../../../shared/interfaces/id-validation.interface';
import {
  ReportMetadataDialog,
  ReportMetadataDialogResult,
} from '../../../ui/report/report-metadata-dialog/report-metadata-dialog';

export type ReportListItem = ReportListItemDto;

@Injectable({ providedIn: 'root' })
export class ReportService implements IdValidation {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly projectService = inject(ProjectService);

  readonly isReady = signal(false);
  readonly reportList = signal<ReportListItem[]>([]);
  readonly selectedReportId = signal<string | null>(null);
  readonly saveComplete = signal<number | null>(null);

  constructor() {
    effect(() => {
      const projectId = this.projectService.currentProject()?.id;
      if (!projectId) {
        this.clearReportList();
        this.isReady.set(false);
        return;
      }

      this.isReady.set(false);
      this.fetchReportList(projectId)
        .then(() => this.isReady.set(true))
        .catch(() => this.isReady.set(false));
    });
  }

  private async fetchReportList(projectId: string): Promise<ReportListItem[]> {
    const params = new HttpParams().set('projectId', projectId);
    const list = await firstValueFrom(
      this.http.get<ReportListItemDto[]>('/api/reports', { params }),
    );
    this.reportList.set(list);
    return list;
  }

  isValidId(id: string): boolean | Promise<boolean> {
    if (this.reportList().length > 0) {
      return this.reportList().some((report) => report.id === id);
    }

    if (!this.isReady()) {
      return false;
    }

    return (async () => {
      const projectId = this.projectService.currentProject()?.id;
      if (!projectId) {
        return false;
      }

      try {
        const list = await this.fetchReportList(projectId);
        return list.some((report) => report.id === id);
      } catch {
        return false;
      }
    })();
  }

  clearReportList(): void {
    this.reportList.set([]);
    this.selectedReportId.set(null);
  }

  async createReport(input: CreateReportDto): Promise<ReportListItem | null> {
    if (!this.isReady()) return null;

    try {
      const created = await firstValueFrom(
        this.http.post<ReportListItemDto>('/api/reports', input),
      );
      this.reportList.update((list) => [...list, created]);
      this.selectedReportId.set(created.id);
      return created;
    } catch {
      return null;
    }
  }

  openCreateReportDialog(currentProject: Project): void {
    this.dialog
      .open(ReportMetadataDialog, {
        width: '520px',
        data: {
          mode: 'create',
          initialName: '',
          initialPeriod: { startDate: null, endDate: null },
        },
      })
      .afterClosed()
      .subscribe((result?: ReportMetadataDialogResult) => {
        if (!result?.name?.trim()) return;
        void (async () => {
          const created = await this.createReport({
            name: result.name,
            projectId: currentProject.id,
            plantId: currentProject.plantMeta?.id,
            reportPeriod: result.reportPeriod,
          });
          if (!created) return;
          this.router.navigate(['/reports', created.id]).then(() => null);
        })();
      });
  }

  async getReport(reportId: string): Promise<ReportDto | null> {
    if (!this.isReady()) return null;

    try {
      return await firstValueFrom(this.http.get<ReportDto>(`/api/reports/${reportId}`));
    } catch {
      return null;
    }
  }

  async saveReport(reportId: string, report: ReportDto): Promise<boolean> {
    if (!this.isReady()) return false;

    try {
      const updated = await firstValueFrom(
        this.http.put<ReportListItemDto>(`/api/reports/${reportId}`, report),
      );
      this.reportList.update((list) =>
        list.map((item) => (item.id === updated.id ? updated : item)),
      );
      this.saveComplete.set(Date.now());
      return true;
    } catch {
      return false;
    }
  }

  async renameReport(
    reportId: string,
    newName: string,
    reportPeriod: ReportPeriodDto,
  ): Promise<boolean> {
    const body: RenameReportDto = {
      name: newName.trim(),
      reportPeriod,
    };
    if (!body.name) return false;

    if (!this.isReady()) return false;

    try {
      const updated = await firstValueFrom(
        this.http.patch<ReportListItemDto>(`/api/reports/${reportId}/name`, body),
      );
      this.reportList.update((list) =>
        list.map((item) => (item.id === updated.id ? updated : item)),
      );
      return true;
    } catch {
      return false;
    }
  }

  async deleteReport(reportId: string): Promise<boolean> {
    if (!this.isReady()) return false;

    try {
      await firstValueFrom(
        this.http.delete<void>(`/api/reports/${reportId}`, { observe: 'response' }),
      );
      this.reportList.update((list) => list.filter((item) => item.id !== reportId));
      if (this.selectedReportId() === reportId) {
        this.selectedReportId.set(null);
      }
      return true;
    } catch {
      return false;
    }
  }

  findById(id: string): ReportListItem | undefined {
    return this.reportList().find((report) => report.id === id);
  }
}


